import { Dispatch, StateUpdater, useState } from 'preact/hooks';
import { MenuPane, MenuProps } from './types';
import { useEsc } from 'client/utils/use-esc';
import { Button } from '@components/buttons';
import { ComponentChildren } from 'preact';
import { IcoArrowLeft, IcoDotsHorizontal, IcoX } from '@components/icons';
import { filepicker } from '@components/filepicker';
import { showError } from '@components/app-error';

export function MenuHeader(props: { close(): void; label: ComponentChildren; isBase?: boolean }) {
  return (
    <header class="flex border-b mb-2 pb-1 pt-2 sticky top-0 inset-x-0 bg-white z-20">
      <Button
        class="flex grow gap-2 px-1 py-1 items-center rounded-lg hover:bg-gray-100 font-medium text-sm capitalize"
        onClick={props.close}
      >
        {!props.isBase && <IcoArrowLeft class="size-4 stroke-2 opacity-75" />}
        {props.label}
        {props.isBase && <IcoX class="size-4 stroke-2 opacity-75 ml-auto" />}
      </Button>
    </header>
  );
}

export function MenuHR() {
  return <hr class="my-1" />;
}

export function MenuItem(props: { label: ComponentChildren; children: ComponentChildren }) {
  return (
    <div class="flex items-center justify-between px-2 gap-4 text-sm">
      <span>{props.label}</span>
      {props.children}
    </div>
  );
}

export const selectedStyle = 'bg-sky-50 border-sky-300 ring-2 ring-sky-300 z-10';

export function MenuChoice<T extends string>(props: {
  label?: ComponentChildren;
  choices: T[];
  choiceUi?(choice: T): ComponentChildren;
  value: T | undefined;
  name: string;
  children?: ComponentChildren;
  onChange(value: T | undefined): void;
}) {
  return (
    <div class="flex items-center justify-between p-1 px-2 gap-4 text-sm transition-all font-medium">
      <span>{props.label}</span>
      <section class="flex">
        <div class="flex text-xs border-collapse min-h-6">
          {props.choices.map((x, i) => {
            const checked = x === props.value;
            return (
              <label
                key={x}
                class={`border-2 inline-flex items-center justify-center font-semibold relative p-1 px-2 transition-all cursor-pointer capitalize ${checked ? selectedStyle : 'text-gray-600 hover:bg-gray-100'} ${i === 0 ? 'rounded-l-md' : ''} ${i !== 0 && !checked ? 'border-l-transparent' : ''} ${i === props.choices.length - 1 && !props.children ? 'rounded-r-md' : ''}`}
              >
                {props.choiceUi?.(x) || x}
                <input
                  class="opacity-0 absolute"
                  type="radio"
                  name={props.name}
                  value={x}
                  checked={checked}
                  onChange={() => {
                    props.onChange(x);
                  }}
                />
              </label>
            );
          })}
          {props.children}
        </div>
      </section>
    </div>
  );
}

export function BtnImg(props: { src: string; onSelect(src: string): void; isSelected: boolean }) {
  return (
    <Button
      onClick={() => props.onSelect(props.src)}
      class={`aspect-video w-20 overflow-hidden rounded-lg shrink-0 ${props.isSelected ? 'ring-sky-600' : 'ring-transparent'} ring-2 ring-offset-2 hover:ring-sky-500 outline-none transition-all`}
    >
      <img src={props.src} class="object-cover" />
    </Button>
  );
}

export function ImageOptions(props: {
  values: string[];
  value: string | undefined;
  onChange(value: string | undefined): void;
}) {
  const curr = props.value;
  const isTemplate = curr && props.values.includes(curr);
  async function pickPhoto() {
    try {
      const result = await filepicker({
        accept: 'image/*',
        sources: ['filepicker', 'takephoto'],
      });

      if (result) {
        props.onChange(result.publicUrl);
      }
    } catch (err) {
      showError(err);
    }
  }

  return (
    <div class="flex flex-col gap-2 px-2 font-medium">
      <span class="text-sm">Image</span>
      <div class="flex flex-wrap max-w-96 gap-2">
        {props.values.map((x) => (
          <BtnImg key={x} isSelected={x === curr} onSelect={props.onChange} src={x} />
        ))}

        {curr && (
          <Button
            class="aspect-video rounded-lg w-20 shrink-0 bg-gray-100 focus:ring-2 ring-offset-2 ring-current inline-flex items-center justify-center leading-0 outline-none hover:bg-gray-200 transition-all uppercase text-xs font-semibold text-gray-500 overflow-hidden relative"
            onClick={() => props.onChange(undefined)}
          >
            None
          </Button>
        )}
        {curr && !isTemplate && <BtnImg isSelected onSelect={props.onChange} src={curr} />}
        <Button
          class="aspect-video rounded-lg w-20 shrink-0 bg-gray-50 focus:ring-2 ring-offset-2 ring-current inline-flex items-center justify-center text-sm leading-0 outline-none hover:bg-gray-100 transition-all border-2 border-dashed border-gray-300 text-gray-600"
          onClick={pickPhoto}
        >
          <IcoDotsHorizontal />
        </Button>
      </div>
    </div>
  );
}

export function SettingsMenu<T>(opts: {
  state: T;
  setState: Dispatch<StateUpdater<T>>;
  close(): void;
  onDelete(): void;
  basePane: MenuPane<T>;
  position: 'top' | 'bottom';
}) {
  const [panes, setPanes] = useState<MenuPane<T>[]>([opts.basePane]);
  const [animation, setAnimation] = useState('an-fade-in-right');
  const props: MenuProps<T> = {
    ...opts,
    close() {
      setAnimation('an-fade-in-right');
      setPanes((xs) => xs.slice(0, -1));
      if (panes.length < 2) {
        opts.close();
      }
    },
    pushPane(pane) {
      setAnimation('an-fade-in-left');
      setPanes((s) => [...s, pane]);
    },
  };

  useEsc(opts.close);

  if (!panes.length) {
    return null;
  }

  return (
    <section
      key={panes.length}
      onClick={(e) => e.stopPropagation()}
      class={`bg-white text-gray-800 rounded-2xl min-w-80 text-left absolute ${opts.position === 'top' ? 'bottom-full -translate-y-4 left-2' : 'top-2 left-2'} shadow-2xl px-2 z-max pb-3 max-h-96 overflow-auto`}
    >
      <div class={`${animation} flex flex-col`}>{panes[panes.length - 1](props)}</div>
    </section>
  );
}
